import React, { useEffect, useRef, useState } from 'react';
import { addTrackForStudent, addTrackFromLink, defaultRemoveVocals, isYouTubeLink, linkForSavedVideo } from '../../services/TrackJobsService';
import { TELL_ANTHONY, availabilityText, linkAvailabilityText, macsOn, removeVocalsForVideo } from '../../services/trackJobsModel';
import LinkTitleNote from './LinkTitleNote';
import useYoutubeLinkTitle from './useYoutubeLinkTitle';
import './AddTrack.css';

// The song's saved video slots, in the dock's order (useYoutubeSearch VIDEO_INTENTS).
const SAVED_VIDEOS = [
	{ key: 'lyrics', label: 'Lyrics' },
	{ key: 'karaoke', label: 'Karaoke' },
	{ key: 'performance', label: 'Performance' },
];

/**
 * "Add a track" (2026-09-26) — the teacher's way to put a new song's track on
 * file mid-lesson, without calling Anthony.
 *
 * STEPPED SINCE LESSON DASHBOARD PASS 3 F2 (2026-09-28), and it lives in the
 * Song panel's Tracks mode rather than under the notes:
 *   1. **Source** — Choose a file (a ClipGrab rip, a Karaoke Version download,
 *      a Logic bounce), one of this song's saved videos (Lyrics / Karaoke /
 *      Performance), or a pasted YouTube link. A file or a saved video IS the
 *      choice, so it moves straight on; a pasted link shows **Next**.
 *   2. **Options** — what was chosen (with **Change**), **Remove vocals** (the
 *      default reads the file name; Karaoke is off, the other videos on), the
 *      line saying who will do the work, **Add track** / **Cancel**.
 *   3. **Working** and 4. **Done** are the job's own lines, which live in the
 *      Tracks mode's "Working now" (JobLines), so the panel can close and the
 *      tab can change while the work goes on.
 *
 * `preset` (D3): the Videos mode's "Track from Karaoke / Performance" opens
 * this at step 2 with that video set; with no `url` it just opens at step 1.
 * A new `preset.nonce` re-applies it.
 *
 * A file and a link are exclusive: choosing one clears the other. The upload
 * lands under the student; a vocals-on job is taken by a studio Mac's helper
 * (functions/trackJobs.js), and the new pills appear in the strip when filed.
 *
 * Plain <button>/<input> on purpose: a `wa-*` boolean prop can stick on under
 * React 18, and this control's disabled state is load-bearing. Every guard is
 * in the handler too, not only on the button.
 */
const AddTrack = ({ studentId, studentName = null, song, uid, workers = [], now = null, preset = null, onAdded = null }) => {
	const [step, setStep] = useState('closed'); // closed | source | options
	const [file, setFile] = useState(null);
	const [link, setLink] = useState('');
	const [linkLabel, setLinkLabel] = useState('');
	const [removeVocals, setRemoveVocals] = useState(true);
	const [busy, setBusy] = useState(false);
	const [progress, setProgress] = useState(0);
	const [error, setError] = useState('');
	const inputRef = useRef(null);

	const presetNonce = preset && preset.nonce;
	useEffect(() => {
		if (!preset || !preset.nonce) return;
		// No url: just open at Source (the strip's "Add a track", or Try again
		// on a failed upload, whose file has to be chosen again).
		if (!preset.url) {
			setError('');
			setStep('source');
			return;
		}
		setFile(null);
		if (inputRef.current) inputRef.current.value = '';
		setLink(preset.url);
		setLinkLabel(preset.label || '');
		setRemoveVocals(preset.removeVocals !== undefined ? Boolean(preset.removeVocals) : defaultRemoveVocals(preset.url));
		setError('');
		setStep('options');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [presetNonce]);

	const anyMacOn = macsOn(workers, now || Date.now()).length > 0;
	// The link's REAL title on the Options step, saved video or pasted alike:
	// "Lyrics video" was all a teacher saw on 2026-10-22, and it was another
	// song. Called before the early return (rules of hooks).
	const linkInfo = useYoutubeLinkTitle(
		step === 'options' && !file && isYouTubeLink(link) ? link : null,
		song?.name
	);

	if (!studentId || !song) return null;
	const linkOk = isYouTubeLink(link);
	const usingLink = !file && link.trim().length > 0;
	const canAdd = (Boolean(file) || linkOk) && !busy && Boolean(uid);

	const reset = () => {
		setFile(null);
		setLink('');
		setLinkLabel('');
		setProgress(0);
		setError('');
		if (inputRef.current) inputRef.current.value = '';
	};

	const close = () => {
		if (busy) return;
		reset();
		setStep('closed');
	};

	const onPick = (e) => {
		const f = e.target.files && e.target.files[0];
		setError('');
		setFile(f || null);
		if (f) {
			setLink('');
			setLinkLabel('');
			setRemoveVocals(defaultRemoveVocals(f.name));
			setStep('options');
		}
	};

	const onLink = (e) => {
		const v = e.target.value;
		setError('');
		setLink(v);
		setLinkLabel('');
		if (v.trim()) {
			setFile(null);
			if (inputRef.current) inputRef.current.value = '';
			setRemoveVocals(defaultRemoveVocals(v));
		}
	};

	const savedVideos = SAVED_VIDEOS.map((v) => ({ ...v, url: linkForSavedVideo(song?.videos?.[v.key]) })).filter((v) => v.url);

	const pickSaved = (v) => {
		if (busy) return;
		setError('');
		setFile(null);
		if (inputRef.current) inputRef.current.value = '';
		setLink(v.url);
		setLinkLabel(`${v.label} video`);
		setRemoveVocals(removeVocalsForVideo(v.key));
		setStep('options');
	};

	const onAdd = async () => {
		if (!canAdd) return;
		setBusy(true);
		setError('');
		try {
			if (file) await addTrackForStudent({ file, studentId, studentName, song, removeVocals, uid, onProgress: setProgress });
			else await addTrackFromLink({ url: link, studentId, studentName, song, removeVocals, uid });
			reset();
			setStep('closed');
			if (onAdded) onAdded();
		} catch (err) {
			console.error('Add a track failed:', err);
			setError((err && err.message) || 'unknown error');
		} finally {
			setBusy(false);
		}
	};

	const chosen = file ? file.name : linkLabel || link;

	return (
		<div className="at" data-testid="add-track">
			{step === 'closed' && (
				<button type="button" className="tp__pill at__open" data-guide="addtrack-open" onClick={() => setStep('source')}>
					<i className="fa-sharp fa-solid fa-plus" aria-hidden="true" />
					Add a track
				</button>
			)}

			{/* The file input stays mounted while the panel is open (either step),
			    so "Change" can go back to it and a test can reach it. */}
			{step !== 'closed' && (
				<div className="at__panel" role="group" aria-label={`Add a track for ${song.name}`}>
					<p className="at__steps" aria-hidden="true">
						<span className={step === 'source' ? 'is-on' : ''}>1 Source</span>
						<span className={step === 'options' ? 'is-on' : ''}>2 Options</span>
					</p>

					<div className={`at__source${step === 'source' ? '' : ' at__source--hidden'}`}>
						<div className="at__row">
							<label className="tp__pill at__choose" data-guide="addtrack-file">
								<i className="fa-sharp fa-solid fa-file-audio" aria-hidden="true" />
								{file ? 'Choose a different file' : 'Choose a file'}
								<input
									ref={inputRef}
									className="at__file"
									type="file"
									accept="audio/*,.mp3,.m4a,.wav,.aif,.aiff"
									onChange={onPick}
									disabled={busy}
								/>
							</label>
						</div>

						{savedVideos.length > 0 && (
							<div className="at__saved" role="group" aria-label="Use a saved video">
								<span className="at__linklabel">or use this song's saved video</span>
								<div className="at__row">
									{savedVideos.map((v) => (
										<button
											key={v.key}
											type="button"
											className={`tp__pill at__savedchip${link === v.url ? ' is-on' : ''}`}
											aria-pressed={link === v.url}
											onClick={() => pickSaved(v)}
											disabled={busy}>
											<i className="fa-brands fa-youtube" aria-hidden="true" />
											{v.label}
										</button>
									))}
								</div>
							</div>
						)}

						<label className="at__linkfield" data-guide="addtrack-link">
							<span className="at__linklabel">or paste a YouTube link</span>
							<input
								className="at__url"
								type="url"
								inputMode="url"
								autoComplete="off"
								spellCheck={false}
								placeholder="https://www.youtube.com/watch?v=…"
								value={link}
								onChange={onLink}
								disabled={busy}
							/>
							{usingLink && !linkOk && (
								<span className="at__hint at__hint--warn">
									{/youtube\.com|youtu\.be/i.test(link)
										? 'That is a YouTube page, not one video. Open the video and copy its link.'
										: 'That is not a YouTube link (youtube.com or youtu.be).'}
								</span>
							)}
						</label>

						{step === 'source' && (
							<div className="at__row">
								<button
									type="button"
									className="tp__pill at__primary"
									data-guide="addtrack-next"
									onClick={() => (file || linkOk) && setStep('options')}
									disabled={!(file || linkOk)}
									aria-disabled={!(file || linkOk)}>
									Next
									<i className="fa-sharp fa-solid fa-arrow-right" aria-hidden="true" />
								</button>
								<button type="button" className="at__link" onClick={close}>
									Cancel
								</button>
							</div>
						)}
					</div>

					{step === 'options' && (
						<>
							<div className="at__chosen">
								<i className={`fa-sharp ${file ? 'fa-solid fa-file-audio' : 'fa-brands fa-youtube'}`} aria-hidden="true" />
								<span className="at__filename" title={chosen}>{chosen}</span>
								<button type="button" className="at__link" onClick={() => !busy && setStep('source')} disabled={busy}>
									Change
								</button>
							</div>
							{usingLink && <LinkTitleNote info={linkInfo} songName={song.name} />}

							<label className="at__toggle" data-guide="addtrack-vocals">
								<input
									type="checkbox"
									checked={removeVocals}
									onChange={(e) => setRemoveVocals(e.target.checked)}
									disabled={busy}
								/>
								<span className="at__togglelabel">Remove vocals</span>
								<span className="at__hint">Turn off for a Karaoke Version or other instrumental file.</span>
							</label>

							{/* Who will do the work (build step 2): said BEFORE the teacher
							    adds, so a slower cloud run is expected rather than a surprise. */}
							{usingLink ? (
								<p className={`at__who${anyMacOn ? '' : ' at__who--cloud'}`} data-testid="add-track-who">
									<i className={`fa-sharp fa-solid ${anyMacOn ? 'fa-desktop' : 'fa-hourglass-half'}`} aria-hidden="true" />
									{linkAvailabilityText(workers, now || Date.now())}
								</p>
							) : (
								removeVocals && (
									<p className={`at__who${anyMacOn ? '' : ' at__who--cloud'}`} data-testid="add-track-who">
										<i className={`fa-sharp fa-solid ${anyMacOn ? 'fa-desktop' : 'fa-cloud'}`} aria-hidden="true" />
										{availabilityText(workers, now || Date.now())}
									</p>
								)
							)}

							{error && (
								<p className="at__error" role="alert">
									The track could not be added. {TELL_ANTHONY}
									<span className="at__detail">{error}</span>
								</p>
							)}

							<div className="at__row">
								<button type="button" className="tp__pill at__primary" data-guide="addtrack-submit" onClick={onAdd} disabled={!canAdd} aria-disabled={!canAdd}>
									<i className={`fa-sharp fa-solid ${busy ? 'fa-spinner-third fa-spin' : 'fa-arrow-up-from-line'}`} aria-hidden="true" />
									{busy ? (file ? `Uploading ${Math.round(progress * 100)}%` : 'Adding…') : linkInfo.mismatch && usingLink ? 'Add it anyway' : 'Add track'}
								</button>
								<button type="button" className="at__link" onClick={close} disabled={busy}>
									Cancel
								</button>
							</div>
						</>
					)}
				</div>
			)}
		</div>
	);
};

export default AddTrack;
